/**
 * Circle to ask: hold Ctrl (or turn on the lasso in the top bar) and draw around anything in the app. A card beside
 * the loop asks what you want to know, and the answer is drawn there (ExplainBoard), with short labels pinned onto
 * the circled figures. A Ctrl+click without a drag circles the block under the pointer.
 *
 * The loop is the user's own: drawn as felt-tip ink that follows the hand (lib/ink.ts), finished off if it stopped a
 * little short, and kept as drawn. What it encloses is read off the page (lib/region.ts) and sent with the question to
 * /api/ai/explain. The loop and pins are drawn in viewport coordinates and follow the page as it scrolls.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLocation } from 'react-router';
import { queryClient, streamSse } from '../lib/api';
import { addInk, closeLoop, dist, handLoop, inkPath, turning, type InkPt } from '../lib/ink';
import { blockAt, boundsOf, readRegion, smoothPath, thin, type Box, type Pt, type Region } from '../lib/region';
import type { Explanation } from '../lib/types';
import { ExplainBoard } from './ExplainBoard';
import { Icons } from './icons';
import { cx, Spinner } from './ui';

/** Dispatched on window by the top bar's lasso button. */
export const CIRCLE_EVENT = 'raremargin:circle';
export const CTRL = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? 'Control' : 'Ctrl';

const isUi = (el: Element | null) => !!el?.closest('[data-circle-ui]');
const delay = (s: number) => ({ '--ct-delay': `${s.toFixed(2)}s` }) as CSSProperties;
const SUGGESTIONS = ['What does this mean?', 'Why did it change?', 'How can I improve this?'];

interface Circle {
  /** The ink as drawn (by the app, for a click), shown on the page. */
  stroke: InkPt[];
  /** The area read: the loop, or the box above an underline. */
  poly: Pt[];
  /** `poly` is that box. */
  box: boolean;
  region: Region;
  scroll0: number;
}

interface Turn {
  q: string;
  ex?: Explanation;
  error?: string;
  lookups: string[];
  pending: boolean;
}

const area = (pts: Pt[]) => Math.abs(pts.reduce((s, p, i) => s + p.x * pts[(i + 1) % pts.length]!.y - pts[(i + 1) % pts.length]!.x * p.y, 0)) / 2;
const mainEl = () => document.querySelector('main');
const rectOf = (b: Box) => ({ x: b.x, y: b.y, width: b.w, height: b.h });

export function CircleToAsk() {
  const [armed, setArmed] = useState<'ctrl' | 'button' | null>(null);
  const [nudge, setNudge] = useState<string | null>(null);
  const [circle, setCircle] = useState<Circle | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [page, setPage] = useState(0);
  const [shift, setShift] = useState(0);
  const [stale, setStale] = useState(false);
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const circleRef = useRef(circle);
  circleRef.current = circle;
  const abort = useRef<AbortController | null>(null);
  // The wet ink, drawn straight onto the page as the pointer moves rather than through React.
  const live = useRef<SVGPathElement>(null);
  const ring = useRef<SVGCircleElement>(null);
  const { pathname } = useLocation();
  const ink = useMemo(() => (circle ? inkPath(circle.stroke, true) : ''), [circle]);

  const close = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setCircle(null);
    setTurns([]);
    setPage(0);
  }, []);

  useEffect(() => close(), [pathname, close]);
  useEffect(() => void document.documentElement.classList.toggle('ct-armed', !!armed), [armed]);
  useEffect(() => {
    if (!nudge) return;
    const t = setTimeout(() => setNudge(null), 2800);
    return () => clearTimeout(t);
  }, [nudge]);
  // Once the loop is on the page, the wet ink it was drawn with goes.
  useLayoutEffect(() => {
    if (circle) live.current?.setAttribute('d', '');
  }, [circle]);

  // The gesture. Listeners run in the capture phase, so a Ctrl+drag never reaches the page underneath.
  useEffect(() => {
    const html = document.documentElement;
    let pts: InkPt[] = [];
    let tip: InkPt | null = null;
    let travelled = 0;
    let active = false;
    let suppress = 0;
    let armTimer = 0;
    let frame = 0;
    let playing = 0;
    let fading = 0;

    const paint = (stroke: InkPt[], done = false) => {
      const el = live.current;
      if (!el) return;
      clearTimeout(fading);
      el.classList.remove('ct-ink-out');
      el.setAttribute('d', inkPath(stroke, done));
    };
    // The ring where the loop began lights up when the pen comes back to it.
    const near = (on: boolean) => ring.current?.classList.toggle('ct-near', on);
    const fade = () => {
      const el = live.current;
      near(false);
      if (!el?.getAttribute('d')) return;
      el.classList.add('ct-ink-out');
      fading = window.setTimeout(() => (el.setAttribute('d', ''), el.classList.remove('ct-ink-out')), 400);
    };
    const end = () => {
      active = false;
      html.classList.remove('ct-drawing');
      near(false);
      cancelAnimationFrame(frame);
      frame = 0;
    };
    const stopPlaying = () => {
      cancelAnimationFrame(playing);
      playing = 0;
    };
    /** Draw `stroke` on from point `from`, as a pen would, then `then`. */
    const play = (stroke: InkPt[], from: number, ms: number, then: () => void) => {
      const t0 = performance.now();
      const tick = () => {
        const k = Math.min(1, (performance.now() - t0) / ms);
        const eased = -(Math.cos(Math.PI * k) - 1) / 2;
        paint(stroke.slice(0, Math.max(2, from + Math.round((stroke.length - from) * eased))));
        if (k < 1) playing = requestAnimationFrame(tick);
        else (playing = 0), then();
      };
      playing = requestAnimationFrame(tick);
    };

    const capture = (stroke: InkPt[], poly: Pt[], box = false) => {
      abort.current?.abort();
      setCircle({ stroke, poly, box, region: readRegion(poly, isUi), scroll0: mainEl()?.scrollTop ?? 0 });
      setTurns([]);
      setPage(0);
      setShift(0);
      setStale(false);
    };

    /** What the hand drew: a tap, a loop, an underline, or something else. */
    const finish = () => {
      const b = boundsOf(pts);
      if (travelled < 12 || (b.w < 14 && b.h < 14)) {
        // A tap: the pen goes round the block under it.
        const el = blockAt(pts[0]!.x, pts[0]!.y, isUi);
        if (!el) return fade();
        const r = el.getBoundingClientRect();
        const loop = handLoop({ x: r.left, y: r.top, w: r.width, h: r.height });
        setArmed(null);
        return play(loop, 0, 560, () => capture(loop, loop));
      }
      const shape = thin(pts, 4);
      const turned = Math.abs(turning(shape));
      const gap = dist(pts[0]!, pts[pts.length - 1]!);
      if (turned > 1.6 * Math.PI || (gap < 0.45 * Math.max(b.w, b.h) && area(shape) > 0.2 * b.w * b.h)) {
        // A loop, kept as drawn. One that stopped short is carried round to where it began.
        setArmed(null);
        const tail = turned < 1.9 * Math.PI ? closeLoop(pts) : [];
        const stroke = [...pts, ...tail];
        if (!tail.length) return capture(stroke, thin(stroke));
        return play(stroke, pts.length, Math.min(260, 90 + tail.length * 8), () => capture(stroke, thin(stroke)));
      }
      if (b.h < 32 && b.w > 40) {
        // An underline: the line just above it.
        setArmed(null);
        const box = [
          { x: b.x - 6, y: b.y - 26 },
          { x: b.x + b.w + 6, y: b.y - 26 },
          { x: b.x + b.w + 6, y: b.y + b.h + 6 },
          { x: b.x - 6, y: b.y + b.h + 6 },
        ];
        return capture(pts, box, true);
      }
      fade();
      setNudge('Draw all the way around what you want to ask about');
      if (armedRef.current === 'ctrl') setArmed(null);
    };

    const pressure = (e: PointerEvent) => (e.pointerType === 'pen' && e.pressure > 0 ? e.pressure : undefined);
    const onDown = (e: PointerEvent) => {
      const layer = (e.target as Element | null)?.closest?.('[data-circle-layer]');
      if (!layer && !(e.ctrlKey && !e.metaKey && !e.altKey)) return;
      if (!layer && isUi(e.target as Element)) return;
      e.preventDefault();
      e.stopPropagation();
      clearTimeout(armTimer);
      stopPlaying();
      setNudge(null);
      active = true;
      travelled = 0;
      pts = [];
      addInk(pts, e.clientX, e.clientY, e.timeStamp, pressure(e));
      tip = pts[0]!;
      ring.current?.setAttribute('cx', String(e.clientX));
      ring.current?.setAttribute('cy', String(e.clientY));
      html.classList.add('ct-drawing');
      paint(pts);
    };
    const onMove = (e: PointerEvent) => {
      if (!active) return;
      e.preventDefault();
      // Every sample the pointer took since the last event, so a fast sweep stays a curve.
      const samples = e.getCoalescedEvents?.() ?? [];
      for (const s of samples.length ? samples : [e]) {
        addInk(pts, s.clientX, s.clientY, s.timeStamp, pressure(s));
        const next = { x: s.clientX, y: s.clientY, t: s.timeStamp, p: pts[pts.length - 1]!.p };
        travelled += dist(tip!, next);
        tip = next;
      }
      if (!frame)
        frame = requestAnimationFrame(() => {
          frame = 0;
          if (!active || !tip) return;
          // The ink runs right up to the pointer.
          paint([...pts, tip]);
          near(travelled > 140 && dist(tip, pts[0]!) < 36);
        });
    };
    const onUp = (e: PointerEvent) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      suppress = performance.now() + 250;
      end();
      if (tip && dist(tip, pts[pts.length - 1]!) > 0.5) pts.push(tip);
      finish();
    };
    const onCancel = () => {
      if (!active) return;
      end();
      fade();
    };
    // The click (or a Mac's Control+click menu) that ends a drawing must not reach the page; the next one may.
    const swallow = (e: Event) => {
      if (!active && performance.now() >= suppress) return;
      e.preventDefault();
      e.stopPropagation();
      if (!active) suppress = 0;
    };
    const onContext = (e: MouseEvent) => {
      if (active || performance.now() < suppress) e.preventDefault();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (active || playing) {
          end();
          stopPlaying();
          fade();
        } else if (armedRef.current) setArmed(null);
        else if (circleRef.current) close();
        return;
      }
      // Ctrl held on its own shows the crosshair; Ctrl+C and friends don't.
      if (e.key === 'Control') {
        if (!e.repeat) armTimer = window.setTimeout(() => setArmed((a) => a ?? 'ctrl'), 350);
      } else {
        clearTimeout(armTimer);
        if (armedRef.current === 'ctrl') setArmed(null);
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== 'Control') return;
      clearTimeout(armTimer);
      if (armedRef.current === 'ctrl') setArmed(null);
    };
    const onBlur = () => {
      clearTimeout(armTimer);
      if (armedRef.current === 'ctrl') setArmed(null);
      if (!active) return;
      end();
      fade();
    };
    const onArm = () => setArmed((a) => (a === 'button' ? null : 'button'));

    const opts = { capture: true, passive: false } as const;
    window.addEventListener('pointerdown', onDown, opts);
    window.addEventListener('pointermove', onMove, opts);
    window.addEventListener('pointerup', onUp, opts);
    window.addEventListener('pointercancel', onCancel, true);
    window.addEventListener('click', swallow, true);
    window.addEventListener('auxclick', swallow, true);
    window.addEventListener('contextmenu', onContext, true);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    window.addEventListener(CIRCLE_EVENT, onArm);
    return () => {
      window.removeEventListener('pointerdown', onDown, opts);
      window.removeEventListener('pointermove', onMove, opts);
      window.removeEventListener('pointerup', onUp, opts);
      window.removeEventListener('pointercancel', onCancel, true);
      window.removeEventListener('click', swallow, true);
      window.removeEventListener('auxclick', swallow, true);
      window.removeEventListener('contextmenu', onContext, true);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener(CIRCLE_EVENT, onArm);
      clearTimeout(armTimer);
      clearTimeout(fading);
      cancelAnimationFrame(frame);
      stopPlaying();
      html.classList.remove('ct-drawing', 'ct-armed');
    };
  }, [close]);

  // The loop and pins move with the page; a resize reflows it, so they are hidden rather than left in the wrong place.
  useEffect(() => {
    if (!circle) return;
    const main = mainEl();
    const onScroll = () => setShift(circle.scroll0 - (main?.scrollTop ?? 0));
    const onResize = () => setStale(true);
    main?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      main?.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [circle]);

  const patch = (i: number, f: (t: Turn) => Turn) => setTurns((ts) => ts.map((t, j) => (j === i ? f(t) : t)));

  async function ask(question: string, index?: number) {
    const c = circleRef.current;
    if (!c) return;
    // Earlier answers on this circle go along, so a follow-up knows what was said.
    const history = turns
      .slice(0, index ?? turns.length)
      .filter((t) => t.ex)
      .slice(-3)
      .map((t) => ({ q: t.q, a: `${t.ex!.title}. ${t.ex!.summary}`.slice(0, 3000) }));
    const i = index ?? turns.length;
    const fresh: Turn = { q: question, lookups: [], pending: true };
    setTurns((ts) => (index === undefined ? [...ts, fresh] : ts.map((t, j) => (j === index ? fresh : t))));
    setPage(i);
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    let answered = false;
    try {
      await streamSse(
        '/api/ai/explain',
        { question, page: c.region.page, headings: c.region.headings, lines: c.region.lines.map(({ id, text }) => ({ id, text })), history },
        (event, raw) => {
          const d = raw as Record<string, unknown>;
          if (event === 'lookup') patch(i, (t) => ({ ...t, lookups: [...t.lookups, String(d.title)] }));
          else if (event === 'explanation') ((answered = true), patch(i, (t) => ({ ...t, ex: d as unknown as Explanation, pending: false })));
          else if (event === 'error') ((answered = true), patch(i, (t) => ({ ...t, error: String(d.message), pending: false })));
        },
        ctl.signal,
      );
      if (!answered && !ctl.signal.aborted) patch(i, (t) => ({ ...t, error: 'The connection closed before the answer arrived. Try again.', pending: false }));
    } catch (e) {
      if (!ctl.signal.aborted) patch(i, (t) => ({ ...t, error: e instanceof Error ? e.message : 'The request failed.', pending: false }));
    } finally {
      if (abort.current === ctl) abort.current = null;
      void queryClient.invalidateQueries({ queryKey: ['/api/ai/status'] });
    }
  }

  return (
    <>
      {(armed || nudge) && <ArmedHint key={nudge ?? 'armed'} mode={armed} nudge={nudge} onCancel={() => setArmed(null)} />}
      {armed === 'button' && <div data-circle-ui data-circle-layer className="fixed inset-0 z-[59] touch-none" />}
      <svg data-circle-ui aria-hidden className="pointer-events-none fixed inset-0 z-[62] h-full w-full overflow-visible">
        <circle ref={ring} r={11} className="ct-close" />
        <path ref={live} className="ct-ink" />
      </svg>
      {circle && !stale && (
        <svg data-circle-ui aria-hidden className="pointer-events-none fixed inset-0 z-[60] h-full w-full overflow-visible">
          <g style={{ transform: `translateY(${shift}px)` }}>
            {circle.box ? (
              <rect {...rectOf(circle.region.bounds)} rx={12} fill="rgb(240 196 46 / 0.09)" className="ct-fade" />
            ) : (
              <path d={smoothPath(circle.poly, true)} fill="rgb(240 196 46 / 0.09)" className="ct-fade" />
            )}
            <path d={ink} className="ct-ink" />
          </g>
        </svg>
      )}
      {circle && (
        <AskCard
          circle={circle}
          shift={shift}
          stale={stale}
          turns={turns}
          page={page}
          onPage={setPage}
          onAsk={(q) => void ask(q)}
          onRetry={(i) => void ask(turns[i]!.q, i)}
          onClose={close}
        />
      )}
    </>
  );
}

/** The pill at the top while drawing is on, or a word of help after a stroke that wasn't a loop. */
function ArmedHint({ mode, nudge, onCancel }: { mode: 'ctrl' | 'button' | null; nudge: string | null; onCancel: () => void }) {
  return (
    <div
      data-circle-ui
      role="status"
      className={cx(
        'animate-fade-up fixed top-20 left-1/2 z-[63] flex -translate-x-1/2 items-center gap-2.5 rounded-full bg-ink py-1.5 pl-3.5 text-[12.5px] text-white shadow-[0_10px_30px_-10px_rgb(27_26_24/0.6)]',
        mode ? 'pr-1.5' : 'pr-4',
      )}
    >
      <Icons.Lasso size={15} className="text-marker" />
      {nudge ?? (mode === 'ctrl' ? 'Keep holding and draw around anything to ask about it' : 'Draw around anything to ask about it')}
      {mode && (
        <button onClick={onCancel} className="rounded-full bg-white/12 px-2 py-0.5 text-[11.5px] text-white/80 hover:bg-white/20">
          Esc
        </button>
      )}
    </div>
  );
}

function AskCard({
  circle,
  shift,
  stale,
  turns,
  page,
  onPage,
  onAsk,
  onRetry,
  onClose,
}: {
  circle: Circle;
  shift: number;
  stale: boolean;
  turns: Turn[];
  page: number;
  onPage: (i: number) => void;
  onAsk: (q: string) => void;
  onRetry: (i: number) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(240);
  const [drawn, setDrawn] = useState<Set<number>>(() => new Set());
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setH(el.offsetHeight));
    ro.observe(el);
    setH(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const sheet = vw < 640;
  const W = Math.min(408, vw - 24);
  const b: Box = { ...circle.region.bounds, y: circle.region.bounds.y + shift };
  // Docked on the right, level with the loop; to the loop's left when the loop is under the dock and there is room.
  let left = vw - W - 16;
  if (b.x + b.w > left - 12 && b.x - 32 - W >= 248) left = b.x - 32 - W;
  const top = Math.max(76, Math.min(b.y - 6, vh - h - 16));
  const beside = !sheet && (left >= b.x + b.w + 8 || left + W <= b.x - 8);

  const turn = turns[page];
  const busy = turns.some((t) => t.pending);
  const lines = circle.region.lines;
  // What was read, as words: chart summaries are for the assistant.
  const preview = lines
    .filter((l) => !/^(Chart\.|Small trend line,)/.test(l.text))
    .slice(0, 4)
    .map((l) => l.text)
    .join(' · ');

  return (
    <>
      {beside && !stale && <Connector poly={circle.poly} shift={shift} to={{ x: left < b.x ? left + W : left, y: top + 22 }} />}
      {turn?.ex && !stale && <Pins ex={turn.ex} region={circle.region} shift={shift} card={sheet ? null : { left, right: left + W }} key={page} />}
      <div
        ref={ref}
        data-circle-ui
        role="dialog"
        aria-label="Ask about what you circled"
        className={cx(
          'ct-board animate-fade-up fixed z-[61] flex flex-col overflow-hidden rounded-2xl border border-ink/[0.09] shadow-[0_28px_70px_-28px_rgb(27_26_24/0.5),0_2px_8px_rgb(27_26_24/0.06)]',
          sheet ? 'max-h-[72vh]' : 'max-h-[min(80vh,760px)] transition-[top,left] duration-300 ease-out',
        )}
        style={sheet ? { left: 12, right: 12, bottom: 12 } : { left, top, width: W }}
      >
        <header className="flex h-10 shrink-0 items-center justify-between gap-2 border-b border-ink/[0.06] bg-white/65 pr-1.5 pl-4 backdrop-blur-sm">
          <span className="flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.1em] text-muted uppercase">
            <Icons.Lasso size={14} className="text-marker" />
            Circle to ask
          </span>
          <span className="flex items-center gap-0.5">
            {turns.length > 1 && (
              <span className="num mr-1 flex items-center text-[11.5px] text-muted">
                <button onClick={() => onPage(page - 1)} disabled={page === 0} aria-label="Previous answer" className="rounded-md p-1 hover:bg-ink/[0.05] disabled:opacity-30">
                  <Icons.Chevron size={13} className="rotate-180" />
                </button>
                {page + 1}/{turns.length}
                <button onClick={() => onPage(page + 1)} disabled={page === turns.length - 1} aria-label="Next answer" className="rounded-md p-1 hover:bg-ink/[0.05] disabled:opacity-30">
                  <Icons.Chevron size={13} />
                </button>
              </span>
            )}
            <button onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-muted hover:bg-ink/[0.05] hover:text-ink">
              <Icons.Close size={14} />
            </button>
          </span>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pt-4 pb-5 scrollbar-thin">
          {!turn ? (
            <div>
              <p className="text-[14px] font-medium text-ink">What would you like to know?</p>
              {lines.length ? (
                <p className="mt-1 line-clamp-2 text-[12px] leading-snug text-muted">
                  You circled <span className="text-ink-2">{preview}</span>
                </p>
              ) : (
                <p className="mt-1 text-[12px] text-bad">There’s nothing to read in that area. Circle some figures or text.</p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-track={s} disabled={!lines.length} onClick={() => onAsk(s)} className={CHIP}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <p className="mb-4 flex items-start gap-2 text-[12.5px] leading-snug text-ink-2">
                <span className="mt-[6px] size-1.5 shrink-0 rounded-full bg-ink/25" />
                <span>“{turn.q}”</span>
              </p>
              {turn.pending && <Sketching lookups={turn.lookups} />}
              {turn.error && (
                <div>
                  <div role="alert" className="rounded-xl border border-bad/20 bg-bad-soft px-3 py-2.5 text-[12.5px] leading-snug text-bad">
                    {turn.error}
                  </div>
                  <button data-track="Try again" onClick={() => onRetry(page)} disabled={busy} className={cx(CHIP, 'mt-2.5')}>
                    Try again
                  </button>
                </div>
              )}
              {turn.ex && (
                <>
                  <ExplainBoard key={page} ex={turn.ex} instant={drawn.has(page)} onDone={() => setDrawn((s) => new Set(s).add(page))} />
                  {turn.ex.followUps.length > 0 && page === turns.length - 1 && (
                    <div className="ct-fade mt-5 flex flex-wrap gap-1.5" style={delay(drawn.has(page) ? 0 : 0.4 * turn.ex.blocks.length + 1.4)}>
                      {/* Written by the model, so not sent to usage counts (no data-track). */}
                      {turn.ex.followUps.map((f) => (
                        <button key={f} disabled={busy} onClick={() => onAsk(f)} className={CHIP}>
                          {f}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <footer className="shrink-0 border-t border-ink/[0.06] bg-white/70 px-3 py-2.5">
          <AskInput key={turns.length} busy={busy} disabled={!lines.length} placeholder={turns.length ? 'Ask a follow-up…' : 'Ask anything about it…'} onAsk={onAsk} />
        </footer>
      </div>
    </>
  );
}

const CHIP =
  'rounded-full border border-ink/10 bg-white/90 px-2.5 py-1 text-left text-[12px] font-medium text-ink-2 transition-colors hover:border-marker hover:bg-white hover:text-ink disabled:pointer-events-none disabled:opacity-45';

function AskInput({ busy, disabled, placeholder, onAsk }: { busy: boolean; disabled: boolean; placeholder: string; onAsk: (q: string) => void }) {
  const [v, setV] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => ref.current?.focus({ preventScroll: true }), []);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!v.trim() || busy || disabled) return;
        onAsk(v.trim());
        setV('');
      }}
      className="flex items-center gap-2"
    >
      <input
        ref={ref}
        value={v}
        onChange={(e) => setV(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        maxLength={1000}
        aria-label="Your question"
        className="h-9 min-w-0 flex-1 rounded-[10px] border border-ink/10 bg-white px-3 text-[13px] text-ink placeholder:text-faint focus:border-marker focus:ring-2 focus:ring-marker/20 focus:outline-none disabled:bg-sunken"
      />
      <button type="submit" aria-label="Ask" data-track="Ask" disabled={busy || disabled || !v.trim()} className="grid size-9 shrink-0 place-items-center rounded-[10px] bg-ink text-white transition-opacity hover:opacity-90 disabled:opacity-35">
        {busy ? <Spinner className="size-4" /> : <Icons.Send size={15} />}
      </button>
    </form>
  );
}

/** While the answer is worked out: a pen scribbling, and what the assistant is reading. */
function Sketching({ lookups }: { lookups: string[] }) {
  const steps = ['Read what you circled', ...lookups];
  return (
    <div aria-live="polite">
      <svg aria-hidden viewBox="0 0 240 56" className="h-12 w-full">
        <path
          d="M6 38C26 8 46 8 58 32S92 54 108 28 146 4 164 28 196 50 212 24 230 14 234 18"
          pathLength={1}
          fill="none"
          stroke="var(--color-marker)"
          strokeWidth={2.4}
          strokeLinecap="round"
          className="ct-scribble"
        />
      </svg>
      <ul className="mt-1 space-y-1.5 text-[12.5px]">
        {steps.map((s, i) => (
          <li key={i} className="animate-fade-up flex items-center gap-2 text-ink-2">
            {i < steps.length - 1 ? <Icons.Check size={13} className="text-good" /> : <Spinner className="size-3.5" />}
            {s}
          </li>
        ))}
        <li className="flex items-center gap-2 text-muted">
          <span className="size-3.5" />
          Then drawing the answer
        </li>
      </ul>
      <div className="mt-4 space-y-2">
        <div className="ct-shimmer h-3 w-3/4 rounded" />
        <div className="ct-shimmer h-3 w-full rounded" />
        <div className="ct-shimmer h-16 w-full rounded-lg" />
      </div>
    </div>
  );
}

/** A pen line from the loop to the card. */
function Connector({ poly, shift, to }: { poly: Pt[]; shift: number; to: Pt }) {
  let from = poly[0]!;
  for (const p of poly) if (Math.hypot(p.x - to.x, p.y + shift - to.y) < Math.hypot(from.x - to.x, from.y + shift - to.y)) from = p;
  const f = { x: from.x, y: from.y + shift };
  const dx = (to.x - f.x) * 0.5;
  return (
    <svg data-circle-ui aria-hidden className="pointer-events-none fixed inset-0 z-[60] h-full w-full overflow-visible">
      <path d={`M${f.x},${f.y}C${f.x + dx},${f.y} ${to.x - dx},${to.y} ${to.x},${to.y}`} pathLength={1} fill="none" stroke="var(--color-marker)" strokeWidth={1.8} strokeLinecap="round" className="ct-draw" style={{ '--ct-dur': '0.5s', opacity: 0.85 } as CSSProperties} />
      <circle cx={to.x} cy={to.y} r={3} fill="var(--color-marker)" className="ct-pop" style={delay(0.45)} />
    </svg>
  );
}

/**
 * Short labels pinned onto circled lines, set like callouts around a diagram: just outside the loop (above for
 * figures in its upper half, below for the rest), spread so they don't overlap or run under the card, each with a
 * leader to its figure. Nothing inside the loop is covered.
 */
function Pins({ ex, region, shift, card }: { ex: Explanation; region: Region; shift: number; card: { left: number; right: number } | null }) {
  const lb = region.bounds;
  const pins = ex.pins.flatMap((p) => {
    const line = region.lines.find((l) => l.id === p.line);
    if (!line) return [];
    const { box } = line;
    const above = box.y + box.h / 2 <= lb.y + lb.h / 2 || lb.y + lb.h + 46 > window.innerHeight;
    const w = Math.min(176, p.label.length * 6.6 + 26);
    const ax = box.x + Math.min(box.w, 280) / 2;
    return [{ label: p.label, w, above, ax, ay: above ? box.y - 2 : box.y + box.h + 2, lx: 0, ly: 0 }];
  });
  if (!pins.length) return null;
  const limit = card && card.left > lb.x ? card.left - 10 : window.innerWidth - 10;
  for (const above of [true, false]) {
    const row = pins.filter((p) => p.above === above).sort((a, b) => a.ax - b.ax);
    let x = -Infinity;
    for (const p of row) {
      p.lx = Math.max(p.ax - p.w / 2, x + 8, 8);
      x = p.lx + p.w;
    }
    const over = x - limit;
    for (const p of row) {
      if (over > 0) p.lx = Math.max(8, p.lx - over);
      p.ly = above ? Math.max(64, lb.y - 34) : lb.y + lb.h + 12;
    }
  }
  const base = 0.9 + ex.blocks.length * 0.35;
  return (
    <div data-circle-ui aria-hidden className="pointer-events-none fixed inset-0 z-[60]" style={{ transform: `translateY(${shift}px)` }}>
      <svg className="absolute inset-0 h-full w-full overflow-visible">
        {pins.map((p, i) => (
          <g key={i}>
            <path d={`M${p.lx + p.w / 2},${p.above ? p.ly + 22 : p.ly}L${p.ax},${p.ay}`} pathLength={1} stroke="var(--color-ink)" strokeOpacity={0.45} strokeWidth={1.2} fill="none" className="ct-draw" style={{ ...delay(base + i * 0.2), '--ct-dur': '0.35s' } as CSSProperties} />
            <circle cx={p.ax} cy={p.ay} r={3} fill="var(--color-marker)" stroke="white" strokeWidth={1.5} className="ct-pop" style={delay(base + i * 0.2 + 0.3)} />
          </g>
        ))}
      </svg>
      {pins.map((p, i) => (
        <div
          key={i}
          className="ct-pop absolute flex h-[22px] items-center gap-1.5 truncate rounded-md border border-ink/10 bg-board px-2 text-[11.5px] font-semibold whitespace-nowrap text-ink shadow-[0_6px_14px_-8px_rgb(27_26_24/0.5)]"
          style={{ left: p.lx, top: p.ly, maxWidth: p.w, ...delay(base + i * 0.2) }}
        >
          <span className="size-1.5 shrink-0 rounded-full bg-marker" />
          {p.label}
        </div>
      ))}
    </div>
  );
}
